/**
 * Service d'authentification via API Backend
 *
 * Communique avec l'API serverless déployée sur Vercel
 */

import axios from 'axios';
import { encryptionService } from './encryption';

// URL de l'API - détection automatique de l'environnement
const getApiBaseUrl = () => {
  // Si une URL API personnalisée est définie
  if (process.env.REACT_APP_API_URL) {
    return process.env.REACT_APP_API_URL;
  }

  // En production (Vercel), utiliser l'URL actuelle
  if (process.env.NODE_ENV === 'production') {
    return window.location.origin + '/api';
  }

  // En développement local, utiliser le proxy
  return '/api';
};

const API_BASE_URL = getApiBaseUrl();

/**
 * Authentifie un utilisateur via l'API
 * @param {string} email - Email de l'utilisateur
 * @param {string} password - Mot de passe de l'utilisateur
 * @returns {Object} Résultat de l'authentification
 */
export const authenticateUser = async (email, password) => {
  try {
    console.log('🔐 Tentative de connexion pour:', email);

    // Valider les entrées
    if (!email || !password) {
      return {
        success: false,
        message: 'Email et mot de passe requis',
      };
    }

    // Appeler l'API de login
    const response = await axios.post(`${API_BASE_URL}/auth/login`, {
      email,
      password,
    });

    const result = response.data;

    if (result.success && result.user) {
      console.log('✅ Utilisateur authentifié:', result.user.email);

      // Générer un token de session sécurisé
      const token = `user_${result.user.id}_${Date.now()}`;

      // Stocker les données de manière sécurisée
      encryptionService.setSecureItem('mongo.auth.token', token);
      encryptionService.setSecureSessionItem('mongo.auth.token', token);
      encryptionService.setSecureItem('user.data', JSON.stringify(result.user));

      console.log('🔐 Session utilisateur créée avec succès');

      return {
        success: true,
        user: result.user,
        message: result.message || 'Connexion réussie',
      };
    }

    return {
      success: false,
      message: result.message || 'Erreur de connexion',
    };
  } catch (error) {
    console.error('❌ Erreur lors de l\'authentification:', error);

    if (error.response) {
      // Le serveur a répondu avec un code d'erreur
      return {
        success: false,
        message: error.response.data?.message || 'Erreur de connexion',
      };
    }

    return {
      success: false,
      message: 'Erreur de connexion. Veuillez réessayer.',
    };
  }
};

/**
 * Inscrit un nouvel utilisateur via l'API
 * @param {Object} userData - Données de l'utilisateur
 * @returns {Object} Résultat de l'inscription
 */
export const registerUser = async (userData) => {
  try {
    console.log('📝 Tentative d\'inscription pour:', userData.email);

    // Valider les données
    if (!userData.email || !userData.password) {
      return {
        success: false,
        message: 'Email et mot de passe requis',
      };
    }

    // Appeler l'API de register
    const response = await axios.post(`${API_BASE_URL}/auth/register`, userData);

    const result = response.data;

    if (result.success && result.user) {
      console.log('✅ Utilisateur créé:', result.user.email);

      return {
        success: true,
        user: result.user,
        message: result.message || 'Inscription réussie',
      };
    }

    return {
      success: false,
      message: result.message || 'Erreur lors de l\'inscription',
    };
  } catch (error) {
    console.error('❌ Erreur lors de l\'inscription:', error);

    if (error.response) {
      return {
        success: false,
        message: error.response.data?.message || 'Erreur lors de l\'inscription',
      };
    }

    return {
      success: false,
      message: 'Erreur lors de l\'inscription. Veuillez réessayer.',
    };
  }
};

/**
 * Vérifie si l'utilisateur est authentifié
 * @returns {boolean} True si authentifié, false sinon
 */
export const isAuthenticated = () => {
  try {
    const localToken = encryptionService.getSecureItem('mongo.auth.token');
    const sessionToken = encryptionService.getSecureSessionItem('mongo.auth.token');

    const isValid = localToken && sessionToken && localToken === sessionToken;

    if (isValid) {
      console.log('🔓 Utilisateur authentifié');
    } else {
      console.log('❌ Utilisateur non authentifié');
    }

    return isValid;
  } catch (error) {
    console.error('❌ Erreur lors de la vérification d\'authentification:', error);
    return false;
  }
};

/**
 * Récupère les données de l'utilisateur connecté
 * @returns {Object|null} Données utilisateur ou null
 */
export const getUserData = () => {
  try {
    if (!isAuthenticated()) {
      return null;
    }

    const encryptedUserData = encryptionService.getSecureItem('user.data');
    if (!encryptedUserData) {
      console.warn('⚠️ Aucune donnée utilisateur trouvée');
      return null;
    }

    const userData = JSON.parse(encryptedUserData);
    console.log('🔓 Données utilisateur récupérées');
    return userData;
  } catch (error) {
    console.error('❌ Erreur lors de la récupération des données utilisateur:', error);
    return null;
  }
};

/**
 * Déconnexion sécurisée
 */
export const logout = () => {
  try {
    encryptionService.removeSecureItem('mongo.auth.token');
    encryptionService.removeSecureItem('user.data');
    encryptionService.removeSecureItem('mongo.auth.token', true); // sessionStorage

    // Nettoyer aussi les anciennes données
    encryptionService.removeSecureItem('sheets.auth.token');
    encryptionService.removeSecureItem('sheets.auth.token', true);
    localStorage.removeItem('user');
    localStorage.removeItem('rememberMe');

    console.log('🔐 Déconnexion sécurisée effectuée');
  } catch (error) {
    console.error('❌ Erreur lors de la déconnexion:', error);
  }
};

export default {
  authenticateUser,
  registerUser,
  isAuthenticated,
  getUserData,
  logout,
};
